import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REQUEST_EXPORT_BATCH_MAX_IDS, type RequestListRow } from '@magimp/shared';

import { listRequests } from '../../../api/requests/index.js';
import { useToastStore } from '../../../stores/toast.js';
import type { RequestFilters } from '../filters.js';
import type { RequestView } from '../types.js';

type RequestSelectionFeedback = 'cap' | 'tooMany' | 'loadError';

interface UseRequestSelectionInput {
  visible: readonly RequestView[];
  filters: Partial<RequestFilters> | null;
}

function compareSelectionOrder(
  left: readonly [string, number],
  right: readonly [string, number],
): number {
  return right[1] - left[1] || right[0].localeCompare(left[0]);
}

function wireCreatedAt(row: RequestListRow): number {
  return Date.parse(row.createdAt);
}

export function useRequestsBatchExport({ visible, filters }: UseRequestSelectionInput) {
  const { t } = useTranslation('requests');
  const showToast = useToastStore((state) => state.show);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState<Map<string, number>>(() => new Map());
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const selectionGeneration = useRef(0);

  // WHY (#2817): a filter change invalidates only an in-flight select-all;
  // manually selected rows intentionally survive the change.
  useEffect(() => {
    selectionGeneration.current += 1;
  }, [filters]);

  const onFeedback = useCallback(
    (feedback: RequestSelectionFeedback) => {
      const key =
        feedback === 'cap'
          ? 'export.bulk.capReached'
          : feedback === 'tooMany'
            ? 'export.bulk.tooMany'
            : 'export.bulk.selectAllError';
      showToast({ tone: 'error', text: t(key) });
    },
    [showToast, t],
  );

  const selectedIds = useMemo(
    () =>
      Array.from(selected.entries())
        .sort(compareSelectionOrder)
        .map(([id]) => id),
    [selected],
  );

  const enter = useCallback(() => setSelectionMode(true), []);
  const clear = useCallback(() => {
    selectionGeneration.current += 1;
    setSelected(new Map());
    setAnchorId(null);
  }, []);
  const exit = useCallback(() => {
    selectionGeneration.current += 1;
    setSelectionMode(false);
    setSelected(new Map());
    setAnchorId(null);
    setExportDialogOpen(false);
  }, []);

  const toggle = useCallback(
    (request: Pick<RequestView, 'id' | 'createdAt'>, shiftKey: boolean) => {
      const targetIndex = visible.findIndex((row) => row.id === request.id);
      const anchorIndex = anchorId ? visible.findIndex((row) => row.id === anchorId) : -1;
      const hasVisibleRange = shiftKey && targetIndex >= 0 && anchorIndex >= 0;
      const range = hasVisibleRange
        ? visible.slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
        : [request];
      const shouldSelect = hasVisibleRange || !selected.has(request.id);
      const next = new Map(selected);
      for (const row of range) {
        if (shouldSelect) next.set(row.id, row.createdAt);
        else next.delete(row.id);
      }
      if (next.size > REQUEST_EXPORT_BATCH_MAX_IDS) {
        onFeedback('cap');
        return;
      }
      setSelected(next);
      setAnchorId(request.id);
    },
    [anchorId, onFeedback, selected, visible],
  );

  const selectAll = useCallback(async () => {
    if (filters === null || selectingAll) return;
    const generation = selectionGeneration.current;
    setSelectingAll(true);
    try {
      const result = await listRequests({ ...filters, cursor: undefined, limit: 21 });
      if (generation !== selectionGeneration.current) return;
      if (result.items.length > REQUEST_EXPORT_BATCH_MAX_IDS) {
        setSelected(new Map());
        setAnchorId(null);
        onFeedback('tooMany');
        return;
      }
      setSelected(new Map(result.items.map((row) => [row.id, wireCreatedAt(row)])));
      setAnchorId(null);
    } catch {
      if (generation === selectionGeneration.current) onFeedback('loadError');
    } finally {
      setSelectingAll(false);
    }
  }, [filters, onFeedback, selectingAll]);

  return {
    selectionMode,
    selectedIds,
    selectedCount: selected.size,
    selectingAll,
    exportDialogOpen,
    selectedRequestIds: new Set(selectedIds),
    enter,
    exit,
    clear,
    toggle,
    selectAll,
    openExport: () => setExportDialogOpen(true),
    closeExport: () => setExportDialogOpen(false),
  };
}

export type RequestsBatchExportController = ReturnType<typeof useRequestsBatchExport>;
